import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Download, FileText, Image, Plus, Trash2, FileSpreadsheet, File, AlertCircle, Edit2, Check, X, Building, FilePlus, Database, Moon, Sun, ArrowUp } from 'lucide-react';
import { generatePDFFromHTML, generateAdvancedPDF } from '../utils/powerPlantPdfGenerator';
import { demoData } from '../utils/powerPlantDemoData';
import Tesseract from 'tesseract.js';
import * as XLSX from 'xlsx';

// Company data from Marine Offer Builder
const companies = {
  'fin-marine': {
    name: 'FIN MARINE & ENERGY SOLUTIONS LTD.',
    address: 'Trust Company Complex, Ajeltake Road, Ajeltake Island, Majuro, Republic of the Marshall Islands MH 96960',
    email: 'info@fin-marine.com',
  },
  'fin-marine-fzco': {
    name: 'FIN MARINE AND ENERGY SOLUTIONS INTERNATIONAL FZCO',
    address: 'HD 140 Floor 23 Sheikh Rashid Tower, Dubai World Trade Centre, Dubai, UAE',
    email: 'info@fin-marine.com',
  },
  'atlantis-marine': {
    name: 'ATLANTIS MARINE AND POWER SOLUTION FZ-LLC',
    address: 'S32-16 Shed No.32 - TP Al Hamra FZ, Al Hamra Industrial Zone-FZ, RAK, United Arab Emirates',
    email: 'kerem@atlantismarinepower.com',
  },
  'used-power-plants': {
    name: 'Used Power Plants',
    address: 'S32 TP, Al Hamra Industrial Zone-FZ, RAK, United Arab Emirates',
    email: 'info@usedpowerplants.com',
  }
};

// Branding presets (store images in public/branding)
const brandingPresets = {
  'fin-marine': {
    label: 'Fin Marine & Energy Solutions',
    logo: `${process.env.PUBLIC_URL}/branding/fin-marine-logo.png`,
    watermark: `${process.env.PUBLIC_URL}/branding/fin-marine-watermark.png`
  },
  'fin-marine-fzco': {
    label: 'Fin Marine FZCO',
    logo: `${process.env.PUBLIC_URL}/branding/fin-marine-fzco-logo.png`,
    watermark: `${process.env.PUBLIC_URL}/branding/fin-marine-fzco-watermark.png`
  },
  'atlantis-marine': {
    label: 'Atlantis Marine',
    logo: `${process.env.PUBLIC_URL}/branding/atlantis-marine-logo.png`,
    watermark: `${process.env.PUBLIC_URL}/branding/atlantis-marine-watermark.png`
  },
  'used-power-plants': {
    label: 'Used Power Plants',
    logo: `${process.env.PUBLIC_URL}/branding/used-power-plants-logo.png`,
    watermark: `${process.env.PUBLIC_URL}/branding/used-power-plants-watermark.png.png`
  }
};

// Mobile-only field label: hidden from lg upwards so the desktop layout is untouched.
const MobileLabel = ({ darkMode, children }) => (
  <span className={`pp-mobile-label lg:hidden block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
    {children}
  </span>
);

const PowerPlantOfferMaker = ({ initialData, onDataChange }) => {
  const getDefaultQuotationNo = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // Helper function to migrate old data format to new array format
  const migrateDataFormat = (data) => {
    if (!data) return null;
    
    const migrated = { ...data };
    
    // Convert paymentTerms from string to array if needed
    if (typeof migrated.paymentTerms === 'string') {
      migrated.paymentTerms = migrated.paymentTerms.trim() ? [migrated.paymentTerms] : [''];
    } else if (!Array.isArray(migrated.paymentTerms)) {
      migrated.paymentTerms = [''];
    }
    
    // Convert additionalNotes from string to array if needed
    if (typeof migrated.additionalNotes === 'string') {
      migrated.additionalNotes = migrated.additionalNotes.trim() ? [migrated.additionalNotes] : [''];
    } else if (!Array.isArray(migrated.additionalNotes)) {
      migrated.additionalNotes = [''];
    }
    
    // Migrate old single equipment format to new equipment array
    if (!migrated.equipment || !Array.isArray(migrated.equipment) || migrated.equipment.length === 0) {
      // Check if old format data exists
      if (migrated.engineManufacturer || migrated.engineType || migrated.engineOutput) {
        migrated.equipment = [{
          id: Date.now(),
          equipmentType: 'Gas Engine',
          quantity: '1',
          engineManufacturer: migrated.engineManufacturer || '',
          engineType: migrated.engineType || '',
          engineOutput: migrated.engineOutput || '',
          yearBuilt: migrated.yearBuilt || '',
          workingHours: migrated.workingHours || '',
          alternatorType: migrated.alternatorType || '',
          voltage: migrated.voltage || '',
          frequency: migrated.frequency || '50Hz',
          powerRating: migrated.powerRating || '',
          speed: migrated.speed || ''
        }];
      } else {
        // Create empty equipment array with one default item
        migrated.equipment = [{
          id: Date.now(),
          equipmentType: 'Gas Engine',
          quantity: '1',
          engineManufacturer: '',
          engineType: '',
          engineOutput: '',
          yearBuilt: '',
          workingHours: '',
          alternatorType: '',
          voltage: '',
          frequency: '50Hz',
          powerRating: '',
          speed: ''
        }];
      }
    }
    
    return migrated;
  };

  const [offerData, setOfferData] = useState(migrateDataFormat(initialData) || {
    quotationNo: getDefaultQuotationNo(),
    date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    clientName: '',
    clientAddress: '',
    clientEmail: '',
    plantTitle: '',
    plantCapacity: '',
    equipment: [{
      id: Date.now(),
      equipmentType: 'Gas Engine',
      quantity: '1',
      engineManufacturer: '',
      engineType: '',
      engineOutput: '',
      yearBuilt: '',
      workingHours: '',
      alternatorType: '',
      voltage: '',
      frequency: '50Hz',
      powerRating: '',
      speed: ''
    }],
    engineManufacturer: '',
    engineType: '',
    engineOutput: '',
    yearBuilt: '',
    workingHours: '',
    alternatorType: '',
    voltage: '',
    frequency: '50Hz',
    powerRating: '',
    speed: '',
    fuelType: 'Natural Gas',
    price: '',
    paymentTerms: [''],
    deliveryTime: '',
    scopeOfSupply: [],
    additionalNotes: [''],
    images: [],
    companyDetails: {
      name: 'Atlantis Marine and Power Solution FZ-LLC',
      addressLine1: 'S32-16 Shed No.32 - TP Al Hamra FZ',
      addressLine2: 'Al Hamra Industrial Zone-FZ',
      addressLine3: 'RAK, United Arab Emirates',
      email: 'info@atlantismarine.ae'
    },
    watermarkImage: null,
    logoImage: null
  });

  const [currentScopeItem, setCurrentScopeItem] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  // Presentational only: which panel is visible below the lg breakpoint.
  // Never read by save/PDF/data code - both panels always stay mounted.
  const [mobileView, setMobileView] = useState('form');
  const fileInputRef = useRef(null);
  const scopeFileInputRef = useRef(null);
  const watermarkInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const formPanelRef = useRef(null);
  const previewPanelRef = useRef(null);

  // Toggle dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Communicate data changes to parent component
  useEffect(() => {
    if (onDataChange) {
      onDataChange(offerData);
    }
  }, [offerData]); // Remove onDataChange from dependencies to prevent loops

  // Handle when initialData changes (e.g., when loading an offer)
  // Use a key prop in the parent component instead of this effect to force re-mount

  const formatPrice = (value) => {
    if (!value) return '';
    
    // Extract currency/prefix (e.g., EUR, USD, $)
    const currencyMatch = value.match(/^([A-Z$£€¥]+\s*)/);
    const currency = currencyMatch ? currencyMatch[0] : '';
    
    // Remove currency and all non-digit characters except decimal point
    const withoutCurrency = value.replace(/^[A-Z$£€¥]+\s*/, '');
    const cleanValue = withoutCurrency.replace(/[^\d.]/g, '');
    
    // Convert to number and format to 2 decimal places
    const numValue = parseFloat(cleanValue) || 0;
    const formattedNum = numValue.toFixed(2);
    
    // Add thousand separators
    const parts = formattedNum.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Join back together with currency
    const formatted = parts.join('.');
    return currency + formatted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'price') {
      // For price field, just store the raw value but display formatted
      setOfferData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setOfferData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCompanyDetailChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      companyDetails: {
        ...prev.companyDetails,
        [field]: value
      }
    }));
  };

  const applyBrandingPreset = (companyKey) => {
    const preset = brandingPresets[companyKey];
    if (!preset) return;

    setOfferData(prev => ({
      ...prev,
      logoImage: preset.logo || prev.logoImage,
      watermarkImage: preset.watermark || prev.watermarkImage
    }));
  };

  // Load company details from predefined companies
  const loadCompanyDetails = (companyKey) => {
    if (companyKey && companies[companyKey]) {
      const company = companies[companyKey];
      const addressLines = company.address.split(', ');
      
      setOfferData(prev => ({
        ...prev,
        companyDetails: {
          name: company.name,
          addressLine1: addressLines[0] || '',
          addressLine2: addressLines[1] || '',
          addressLine3: addressLines.slice(2).join(', ') || '',
          email: company.email
        }
      }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [];

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newImages.push({
          id: Date.now() + Math.random(),
          url: event.target.result,
          name: file.name,
          order: 0 // Will be set properly when added
        });
        
        if (newImages.length === files.length) {
          setOfferData(prev => {
            const currentMaxOrder = prev.images.length > 0 ? Math.max(...prev.images.map(img => img.order || 0)) : 0;
            const imagesWithOrder = newImages.map((img, index) => ({
              ...img,
              order: currentMaxOrder + index + 1
            }));
            
            return {
            ...prev,
              images: [...prev.images, ...imagesWithOrder]
            };
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleWatermarkUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setOfferData(prev => ({
          ...prev,
          watermarkImage: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setOfferData(prev => ({
          ...prev,
          logoImage: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (id) => {
    setOfferData(prev => {
      const filteredImages = prev.images.filter(img => img.id !== id);
      // Reorder remaining images
      const reorderedImages = filteredImages
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((img, index) => ({
          ...img,
          order: index + 1
        }));

      return {
        ...prev,
        images: reorderedImages
      };
    });
  };

  const rotateImage = async (id, degrees) => {
    // Find the image to rotate
    const imageToRotate = offerData.images.find(img => img.id === id);
    if (!imageToRotate) {
      console.error('Image not found:', id);
      return;
    }

    try {
      // For images that are already data URLs (not from external sources), rotate directly
      if (imageToRotate.url.startsWith('data:')) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const image = document.createElement('img');
        
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = imageToRotate.url;
        });
        
        // Calculate new dimensions after rotation
        if (Math.abs(degrees) === 90 || Math.abs(degrees) === 270) {
          canvas.width = image.height;
          canvas.height = image.width;
        } else {
          canvas.width = image.width;
          canvas.height = image.height;
        }
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Rotate and draw
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(image, -image.width / 2, -image.height / 2);
        ctx.restore();
        
        // Get rotated image
        const isPng = imageToRotate.url.includes('image/png');
        const rotatedUrl = canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', 0.95);
        
        // Update state
        setOfferData(prev => ({
          ...prev,
          images: prev.images.map(img => 
            img.id === id 
              ? { ...img, url: rotatedUrl }
              : img
          )
        }));
        
        console.log('Image rotated successfully');
      } else {
        // For external URLs (Supabase), we need to fetch and convert to data URL first
        console.log('Fetching external image for rotation...');
        
        try {
          // Fetch the image without CORS
          const response = await fetch(imageToRotate.url);
          const blob = await response.blob();
          
          // Convert to data URL
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          // Now rotate the data URL
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const image = document.createElement('img');
          
          await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
            image.src = dataUrl;
          });
          
          // Calculate new dimensions
          if (Math.abs(degrees) === 90 || Math.abs(degrees) === 270) {
            canvas.width = image.height;
            canvas.height = image.width;
          } else {
            canvas.width = image.width;
            canvas.height = image.height;
          }
          
          // Clear and rotate
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((degrees * Math.PI) / 180);
          ctx.drawImage(image, -image.width / 2, -image.height / 2);
          ctx.restore();
          
          // Get result
          const rotatedUrl = canvas.toDataURL('image/jpeg', 0.95);
          
          // Update state with rotated data URL
          setOfferData(prev => ({
            ...prev,
            images: prev.images.map(img => 
              img.id === id 
                ? { ...img, url: rotatedUrl }
                : img
            )
          }));
          
          console.log('External image rotated successfully');
        } catch (error) {
          console.error('Failed to fetch/rotate external image:', error);
          alert('Unable to rotate this image. The image may need to be re-uploaded.');
        }
      }
    } catch (error) {
      console.error('Error rotating image:', error);
      alert('Failed to rotate image. Please try again.');
    }
  };

  // Drag and drop handlers
  const [draggedImage, setDraggedImage] = useState(null);

  const handleDragStart = (e, imageId) => {
    setDraggedImage(imageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetImageId) => {
    e.preventDefault();
    
    if (draggedImage && draggedImage !== targetImageId) {
      setOfferData(prev => {
        const images = [...prev.images];
        const draggedIndex = images.findIndex(img => img.id === draggedImage);
        const targetIndex = images.findIndex(img => img.id === targetImageId);
        
        if (draggedIndex > -1 && targetIndex > -1) {
          // Remove dragged item and insert at target position
          const [draggedItem] = images.splice(draggedIndex, 1);
          images.splice(targetIndex, 0, draggedItem);
          
          // Reorder all images
          const reorderedImages = images.map((img, index) => ({
            ...img,
            order: index + 1
          }));
          
          return {
            ...prev,
            images: reorderedImages
          };
        }
        
        return prev;
      });
    }
    
    setDraggedImage(null);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  const addScopeItem = () => {
    if (currentScopeItem.trim()) {
      setOfferData(prev => ({
        ...prev,
        scopeOfSupply: [...prev.scopeOfSupply, {
          description: currentScopeItem.trim(),
          quantity: '1',
          unit: 'Set'
        }]
      }));
      setCurrentScopeItem('');
    }
  };

  const removeScopeItem = (index) => {
    setOfferData(prev => ({
      ...prev,
      scopeOfSupply: prev.scopeOfSupply.filter((_, i) => i !== index)
    }));
  };

  const startEditingScopeItem = (index) => {
    setEditingIndex(index);
    setEditingValue(offerData.scopeOfSupply[index].description);
  };

  const saveEditingScopeItem = () => {
    if (editingValue.trim()) {
      const newScope = [...offerData.scopeOfSupply];
      newScope[editingIndex] = {
        ...newScope[editingIndex],
        description: editingValue.trim()
      };
      setOfferData(prev => ({
        ...prev,
        scopeOfSupply: newScope
      }));
    }
    setEditingIndex(-1);
    setEditingValue('');
  };

  const updateScopeQuantity = (index, quantity) => {
    const newScope = [...offerData.scopeOfSupply];
    newScope[index] = {
      ...newScope[index],
      quantity: quantity
    };
    setOfferData(prev => ({
      ...prev,
      scopeOfSupply: newScope
    }));
  };

  const updateScopeUnit = (index, unit) => {
    const newScope = [...offerData.scopeOfSupply];
    newScope[index] = {
      ...newScope[index],
      unit: unit
    };
    setOfferData(prev => ({
      ...prev,
      scopeOfSupply: newScope
    }));
  };

  const cancelEditingScopeItem = () => {
    setEditingIndex(-1);
    setEditingValue('');
  };

  const extractTextFromImage = async (file) => {
    setScopeError('Processing image with OCR...');
    
    try {
      // Convert file to URL for Tesseract
      const imageUrl = URL.createObjectURL(file);
      
      // Perform OCR
      const result = await Tesseract.recognize(
        imageUrl,
        'eng',
        {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              const progress = Math.round(m.progress * 100);
              setScopeError(`Recognizing text... ${progress}%`);
            }
          }
        }
      );
      
      // Clean up URL
      URL.revokeObjectURL(imageUrl);
      
      // Parse the recognized text
      const text = result.data.text;
      const lines = text.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 3); // Filter out very short lines
      
      // Try to intelligently parse the lines
      const items = [];
      for (const line of lines) {
        // Skip headers or common non-item text
        if (line.match(/^(no|item|description|qty|quantity|unit|total|page|scope|supply)/i)) {
          continue;
        }
        
        // Try to extract quantity and unit if present
        const qtyMatch = line.match(/(\d+)\s*(pcs|pc|unit|units|set|sets|lot|lots|ea|package|packages|service)?\s*$/i);
        
        if (qtyMatch) {
          const description = line.substring(0, line.indexOf(qtyMatch[0])).trim();
          if (description) {
            // Normalize unit
            let unit = qtyMatch[2] || 'Set';
            unit = unit.toLowerCase();
            if (unit === 'pcs' || unit === 'pc') unit = 'Pcs';
            else if (unit === 'unit' || unit === 'units') unit = 'Unit';
            else if (unit === 'set' || unit === 'sets') unit = 'Set';
            else if (unit === 'lot' || unit === 'lots') unit = 'Lot';
            else if (unit === 'package' || unit === 'packages') unit = 'Package';
            else if (unit === 'ea') unit = 'EA';
            else if (unit === 'service') unit = 'Service';
            else unit = 'Set';
            
            items.push({
              description: description,
              quantity: qtyMatch[1] || '1',
              unit: unit
            });
          }
        } else if (line.length > 5) {
          // Add as item without quantity
          items.push({
            description: line,
            quantity: '1',
            unit: 'Set'
          });
        }
      }
      
      setScopeError('');
      return items;
    } catch (error) {
      console.error('OCR Error:', error);
      setScopeError('Error reading text from image. Please try again.');
      return [];
    }
  };

  const parseExcelData = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          const items = [];
          let headerRow = null;
          let descCol = -1, qtyCol = -1, unitCol = -1;
          
          // Find header row and identify columns
          for (let i = 0; i < Math.min(jsonData.length, 10); i++) {
            const row = jsonData[i];
            if (Array.isArray(row)) {
              const rowStr = row.join(' ').toLowerCase();
              if (rowStr.includes('description') || rowStr.includes('item') || rowStr.includes('scope')) {
                headerRow = i;
                
                // Find column indices
                row.forEach((cell, idx) => {
                  const cellStr = String(cell).toLowerCase();
                  if (cellStr.includes('description') || cellStr.includes('item') || cellStr.includes('scope')) {
                    descCol = idx;
                  } else if (cellStr.includes('qty') || cellStr.includes('quantity')) {
                    qtyCol = idx;
                  } else if (cellStr.includes('unit')) {
                    unitCol = idx;
                  }
                });
                break;
              }
            }
          }
          
          // If no header found, assume first column is description
          if (descCol === -1) {
            descCol = 0;
          }
          
          // Parse data rows
          const startRow = headerRow !== null ? headerRow + 1 : 0;
          
          for (let i = startRow; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (Array.isArray(row) && row.length > 0) {
              const description = row[descCol];
              
              if (description && String(description).trim().length > 3) {
                const item = {
                  description: String(description).trim(),
                  quantity: qtyCol !== -1 && row[qtyCol] ? String(row[qtyCol]).trim() : '1',
                  unit: unitCol !== -1 && row[unitCol] ? String(row[unitCol]).trim() : 'Set'
                };
                
                // Skip if it looks like a total or subtotal row
                if (!item.description.match(/^(total|subtotal|grand total)/i)) {
                  items.push(item);
                }
              }
            }
          }
          
          resolve(items);
        } catch (error) {
          console.error('Excel parsing error:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  const parseCSVData = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          const items = [];
          let headerIndex = -1;
          let descCol = 0, qtyCol = -1, unitCol = -1;
          
          // Find header row
          for (let i = 0; i < Math.min(lines.length, 5); i++) {
            const line = lines[i].toLowerCase();
            if (line.includes('description') || line.includes('item') || line.includes('scope')) {
              headerIndex = i;
              
              // Parse header to find column positions
              const headers = lines[i].split(',').map(h => h.trim().toLowerCase());
              headers.forEach((header, idx) => {
                if (header.includes('description') || header.includes('item') || header.includes('scope')) {
                  descCol = idx;
                } else if (header.includes('qty') || header.includes('quantity')) {
                  qtyCol = idx;
                } else if (header.includes('unit')) {
                  unitCol = idx;
                }
              });
              break;
            }
          }
          
          // Parse data rows
          const startIndex = headerIndex !== -1 ? headerIndex + 1 : 0;
          
          for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line) {
              const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
              const description = columns[descCol];
              
              if (description && description.length > 3) {
                const item = {
                  description: description,
                  quantity: qtyCol !== -1 && columns[qtyCol] ? columns[qtyCol] : '1',
                  unit: unitCol !== -1 && columns[unitCol] ? columns[unitCol] : 'Set'
                };
                
                // Skip totals
                if (!item.description.match(/^(total|subtotal|grand total)/i)) {
                  items.push(item);
                }
              }
            }
          }
          
          resolve(items);
        } catch (error) {
          console.error('CSV parsing error:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };
      
      reader.readAsText(file);
    });
  };

  const parseWordDocument = async (file) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const simulatedData = [
          "Complete Gas Power Plant Installation",
          "Civil Works and Foundation",
          "Mechanical Installation",
          "Electrical Installation", 
          "Control and Instrumentation",
          "Commissioning and Testing",
          "Training and Documentation",
          "Spare Parts Package",
          "Maintenance Tools",
          "Safety Equipment"
        ];
        resolve(simulatedData);
      }, 1000);
    });
  };

  const handleScopeFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setScopeLoading(true);
    setScopeError('');

    try {
      let extractedItems = [];
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (fileType.includes('image') || fileName.match(/\.(jpg|jpeg|png|gif|bmp)$/)) {
        extractedItems = await extractTextFromImage(file);
      } else if (fileName.match(/\.(xlsx|xls)$/)) {
        extractedItems = await parseExcelData(file);
      } else if (fileName.match(/\.csv$/)) {
        extractedItems = await parseCSVData(file);
      } else if (fileName.match(/\.(doc|docx)$/)) {
        extractedItems = await parseWordDocument(file);
      } else {
        setScopeError('Unsupported file type. Please use Word, Excel, CSV, or image files.');
        setScopeLoading(false);
        return;
      }

      // Check if items are already in the correct format
      const formattedItems = extractedItems.map(item => {
        if (typeof item === 'object' && item.description) {
          // Already in correct format from OCR or Excel parsing
          return item;
        } else {
          // Legacy format - convert string to object
          return {
            description: item,
            quantity: '1',
            unit: 'Set'
          };
        }
      });

      setOfferData(prev => ({
        ...prev,
        scopeOfSupply: [...prev.scopeOfSupply, ...formattedItems]
      }));

      setScopeLoading(false);
    } catch (error) {
      setScopeError('Error processing file. Please try again.');
      setScopeLoading(false);
    }
  };

  const generatePDF = async () => {
    setPdfGenerating(true);
    try {
      const element = document.getElementById('offer-preview');
      const fileName = `power-plant-quotation-${offerData.quotationNo || 'draft'}-${Date.now()}.pdf`;
      await generatePDFFromHTML(element, fileName, offerData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const generateAdvancedPDFDoc = async () => {
    setPdfGenerating(true);
    try {
      // Let the disabled "Generating…" label paint before the PDF work runs.
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      await generateAdvancedPDF(offerData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const loadDemoData = () => {
    setOfferData(prev => ({
      ...prev,
      ...demoData,
      date: prev.date // Keep current date
    }));
  };

  // Helper function for input classes
  const getInputClasses = (additionalClasses = '') => {
    return `w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300'
    } ${additionalClasses}`;
  };

  // Helper function for label classes
  const getLabelClasses = () => {
    return `block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`;
  };

  // Helper functions for bullet point management
  const addBulletPoint = useCallback((field) => {
    setOfferData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }));
  }, []);

  const removeBulletPoint = useCallback((field, index) => {
    setOfferData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  }, []);

  const updateBulletPoint = useCallback((field, index, value) => {
    setOfferData(prev => {
      const currentArray = prev[field] || [];
      // Only update if the value actually changed
      if (currentArray[index] === value) {
        return prev;
      }
      return {
        ...prev,
        [field]: currentArray.map((item, i) => i === index ? value : item)
      };
    });
  }, []);

  // Equipment management functions
  const addEquipment = () => {
    const newEquipment = {
      id: Date.now() + Math.random(),
      equipmentType: 'Gas Engine',
      quantity: '1',
      engineManufacturer: '',
      engineType: '',
      engineOutput: '',
      yearBuilt: '',
      workingHours: '',
      alternatorType: '',
      voltage: '',
      frequency: '50Hz',
      powerRating: '',
      speed: ''
    };
    setOfferData(prev => ({
      ...prev,
      equipment: [...(prev.equipment || []), newEquipment]
    }));
  };

  const removeEquipment = (id) => {
    setOfferData(prev => ({
      ...prev,
      equipment: (prev.equipment || []).filter(eq => eq.id !== id)
    }));
  };

  const updateEquipment = (id, field, value) => {
    setOfferData(prev => ({
      ...prev,
      equipment: (prev.equipment || []).map(eq => 
        eq.id === id ? { ...eq, [field]: value } : eq
      )
    }));
  };

  // Mobile-only panel switcher. Scrolls the page to the top of the panel that
  // was just shown so the user always lands at a predictable place.
  const showMobilePanel = (view) => {
    setMobileView(view);
    const panel = view === 'preview' ? previewPanelRef.current : formPanelRef.current;
    window.requestAnimationFrame(() => {
      if (!panel) return;
      const top = panel.getBoundingClientRect().top + window.scrollY - 8;
      window.scrollTo({ top: Math.max(0, top) });
    });
  };

  const mobileTabClasses = (view) => {
    const active = mobileView === view;
    if (active) {
      return 'flex items-center justify-center gap-2 rounded-md px-3 py-2 text-base font-semibold bg-blue-600 text-white shadow-sm';
    }
    return `flex items-center justify-center gap-2 rounded-md px-3 py-2 text-base font-semibold border ${
      darkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
    }`;
  };

  return (
    <div className={`pp-builder min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} p-6 max-sm:px-3 max-sm:pt-3 transition-colors duration-200`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8 max-lg:flex-col max-lg:items-stretch max-lg:gap-3 max-lg:mb-3">
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'} flex items-center gap-3 max-lg:text-2xl max-sm:text-xl`}>
            <FileText style={{color: '#2B3E5C'}} />
            Power Plant Offer Maker
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors max-lg:flex max-lg:items-center max-lg:justify-center`}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={loadDemoData}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 max-lg:flex-1 max-lg:justify-center"
            >
              <Database size={18} />
              Load Demo Data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-lg:gap-4">
          {/* Input Form */}
          <div
            ref={formPanelRef}
            className={`${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'} rounded-lg shadow-md p-6 overflow-auto max-h-[90vh] max-lg:overflow-visible max-lg:max-h-none max-lg:p-4 ${mobileView === 'form' ? '' : 'max-lg:hidden'}`}
          >
            <h2 className="text-xl font-semibold mb-4">Offer Details</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Quotation No.
                  </label>
                  <input
                    type="text"
                    name="quotationNo"
                    inputMode="numeric"
                    value={offerData.quotationNo}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'
                    }`}
                    placeholder="24101801"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Date
                  </label>
                  <input
                    type="text"
                    name="date"
                    value={offerData.date}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={getLabelClasses()}>
                  Client Name
                </label>
                <input
                  type="text"
                  name="clientName"
                  value={offerData.clientName}
                  onChange={handleInputChange}
                  className={getInputClasses()}
                  placeholder="Company Name"
                />
              </div>

              <div>
                <label className={getLabelClasses()}>
                  Client Address
                </label>
                <textarea
                  name="clientAddress"
                  value={offerData.clientAddress}
                  onChange={handleInputChange}
                  className={getInputClasses()}
                  rows="2"
                  placeholder="Full address"
                />
              </div>

              <div>
                <label className={getLabelClasses()}>
                  Client Email
                </label>
                <input
                  type="email"
                  name="clientEmail"
                  inputMode="email"
                  value={offerData.clientEmail}
                  onChange={handleInputChange}
                  className={getInputClasses()}
                  placeholder="client@email.com"
                />
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">Plant Specifications</h3>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <MobileLabel darkMode={darkMode}>Plant Title</MobileLabel>
                    <input
                      type="text"
                      name="plantTitle"
                      value={offerData.plantTitle}
                      onChange={handleInputChange}
                      className={getInputClasses()}
                      placeholder="Plant Title (e.g., Combined Cycle Power Plant)"
                    />
                  </div>
                  
                  <div>
                    <MobileLabel darkMode={darkMode}>Capacity</MobileLabel>
                    <input
                      type="text"
                      name="plantCapacity"
                      value={offerData.plantCapacity}
                      onChange={handleInputChange}
                      className={getInputClasses()}
                      placeholder="Capacity (e.g., 17.2 MW)"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                      Fuel Type
                    </label>
                    <select
                      name="fuelType"
                      value={offerData.fuelType}
                      onChange={handleInputChange}
                      className={getInputClasses()}
                    >
                      <option value="Natural Gas">Natural Gas</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Heavy Fuel Oil">Heavy Fuel Oil</option>
                      <option value="Dual Fuel">Dual Fuel</option>
                      <option value="Biogas">Biogas</option>
                      <option value="LPG">LPG</option>
                      <option value="Syngas">Syngas</option>
                    </select>
                  </div>
                </div>
                
                {/* Equipment Section */}
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-3 max-md:flex-col max-md:items-stretch max-md:gap-2">
                    <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} max-md:text-base`}>
                      Equipment Details
                    </h4>
                    <button
                      type="button"
                      onClick={addEquipment}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1 text-sm max-md:justify-center max-md:text-base"
                    >
                      <Plus size={16} />
                      Add Equipment
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {(offerData.equipment || []).map((eq, eqIndex) => (
                      <div 
                        key={eq.id} 
                        className={`p-4 rounded-lg border-2 ${
                          darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3 max-md:items-center">
                          <div className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'} max-md:text-base`}>
                            Equipment #{eqIndex + 1}
                          </div>
                          {(offerData.equipment || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEquipment(eq.id)}
                              className="px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1 text-xs max-md:text-sm max-md:px-3"
                            >
                              <Trash2 size={14} />
                              Remove
                            </button>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                            <div>
                              <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                Equipment Type
                              </label>
                              <select
                                value={eq.equipmentType}
                                onChange={(e) => updateEquipment(eq.id, 'equipmentType', e.target.value)}
                                className={getInputClasses()}
                              >
                                <option value="Gas Engine">Gas Engine</option>
                                <option value="Diesel Engine">Diesel Engine</option>
                                <option value="Gas Turbine">Gas Turbine</option>
                                <option value="Steam Turbine">Steam Turbine</option>
                                <option value="Wind Turbine">Wind Turbine</option>
                                <option value="Hydro Turbine">Hydro Turbine</option>
                                <option value="Generator">Generator</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                Quantity
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={eq.quantity}
                                onChange={(e) => updateEquipment(eq.id, 'quantity', e.target.value)}
                                className={getInputClasses()}
                                placeholder="e.g., 2"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                            <div>
                              <MobileLabel darkMode={darkMode}>Manufacturer</MobileLabel>
                              <input
                                type="text"
                                value={eq.engineManufacturer}
                                onChange={(e) => updateEquipment(eq.id, 'engineManufacturer', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Manufacturer (e.g., Wartsila)"
                              />
                            </div>
                            
                            <div>
                              <MobileLabel darkMode={darkMode}>Model</MobileLabel>
                              <input
                                type="text"
                                value={eq.engineType}
                                onChange={(e) => updateEquipment(eq.id, 'engineType', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Model (e.g., 20V34SG)"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                            <div>
                              <MobileLabel darkMode={darkMode}>Output (KW)</MobileLabel>
                              <input
                                type="text"
                                value={eq.engineOutput}
                                onChange={(e) => updateEquipment(eq.id, 'engineOutput', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Output (e.g., 10000 KW)"
                              />
                            </div>
                            
                            <div>
                              <MobileLabel darkMode={darkMode}>Year Built</MobileLabel>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={eq.yearBuilt}
                                onChange={(e) => updateEquipment(eq.id, 'yearBuilt', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Year Built"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <MobileLabel darkMode={darkMode}>Working Hours</MobileLabel>
                            <input
                              type="text"
                              value={eq.workingHours}
                              onChange={(e) => updateEquipment(eq.id, 'workingHours', e.target.value)}
                              className={getInputClasses()}
                              placeholder="Working Hours"
                            />
                          </div>
                          
                          <div>
                            <MobileLabel darkMode={darkMode}>Alternator / Generator Type</MobileLabel>
                            <input
                              type="text"
                              value={eq.alternatorType}
                              onChange={(e) => updateEquipment(eq.id, 'alternatorType', e.target.value)}
                              className={getInputClasses()}
                              placeholder="Alternator/Generator Type"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                            <div>
                              <MobileLabel darkMode={darkMode}>Voltage (KV)</MobileLabel>
                              <input
                                type="text"
                                value={eq.voltage}
                                onChange={(e) => updateEquipment(eq.id, 'voltage', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Voltage (e.g., 11 KV)"
                              />
                            </div>
                            
                            <div>
                              <MobileLabel darkMode={darkMode}>Frequency</MobileLabel>
                              <input
                                type="text"
                                value={eq.frequency}
                                onChange={(e) => updateEquipment(eq.id, 'frequency', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Frequency (e.g., 50Hz)"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
                            <div>
                              <MobileLabel darkMode={darkMode}>Power Rating</MobileLabel>
                              <input
                                type="text"
                                value={eq.powerRating}
                                onChange={(e) => updateEquipment(eq.id, 'powerRating', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Power Rating (e.g., 12163 KVa)"
                              />
                            </div>
                            
                            <div>
                              <MobileLabel darkMode={darkMode}>Speed (rpm)</MobileLabel>
                              <input
                                type="text"
                                value={eq.speed}
                                onChange={(e) => updateEquipment(eq.id, 'speed', e.target.value)}
                                className={getInputClasses()}
                                placeholder="Speed (e.g., 750 rpm)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className="font-semibold mb-3">Commercial Terms</h3>
                
                <div className="space-y-3">
                  <div>
                  <MobileLabel darkMode={darkMode}>Price</MobileLabel>
                  <input
                    type="text"
                    name="price"
                    value={offerData.price}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      // Format price on blur for better display
                      const formatted = formatPrice(e.target.value);
                      if (formatted !== e.target.value) {
                        setOfferData(prev => ({
                          ...prev,
                          price: formatted
                        }));
                      }
                    }}
                    className={getInputClasses()}
                    placeholder="Price (e.g., EUR 4,750,000)"
                  />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2 max-md:flex-col max-md:items-stretch max-md:gap-2">
                      <label className={getLabelClasses()}>Payment Terms</label>
                      <button
                        type="button"
                        onClick={() => addBulletPoint('paymentTerms')}
                        className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1 text-sm max-md:justify-center max-md:text-base"
                      >
                        <Plus size={16} />
                        Add Term
                      </button>
                    </div>
                    {(Array.isArray(offerData.paymentTerms) ? offerData.paymentTerms : ['']).map((term, index) => (
                      <div key={`payment-term-${index}`} className="flex gap-2 mb-2">
                        <div className="flex-1 flex items-center gap-2 max-lg:min-w-0">
                          <span className={`text-sm max-lg:hidden ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>•</span>
                          <input
                            type="text"
                            value={term}
                            onChange={(e) => updateBulletPoint('paymentTerms', index, e.target.value)}
                    className={getInputClasses('max-lg:min-w-0')}
                            placeholder="Enter payment term..."
                          />
                        </div>
                        {Array.isArray(offerData.paymentTerms) && offerData.paymentTerms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBulletPoint('paymentTerms', index)}
                            className="px-2 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center max-md:justify-center max-md:shrink-0"
                            aria-label="Remove payment term"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* lg:pt-2 keeps the desktop gap identical: the bare input used to
                      sit next to the payment block without margin collapsing. */}
                  <div className="lg:pt-2">
                    <MobileLabel darkMode={darkMode}>Delivery Time</MobileLabel>
                    <input
                      type="text"
                      name="deliveryTime"
                      value={offerData.deliveryTime}
                      onChange={handleInputChange}
                      className={getInputClasses()}
                      placeholder="Delivery Time"
                    />
                  </div>
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className="font-semibold mb-3">Scope of Supply</h3>
                
                <div className={`${darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-md p-3 mb-3`}>
                  <p className={`text-sm ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                    Upload files or type manually. Items are editable after adding.
                  </p>
                </div>

                <input
                  ref={scopeFileInputRef}
                  type="file"
                  accept=".doc,.docx,.xls,.xlsx,.csv,image/*,.txt"
                  onChange={handleScopeFileUpload}
                  className="hidden"
                />
                
                <div className="grid grid-cols-2 gap-2 mb-3 max-md:grid-cols-1">
                  <button
                    onClick={() => scopeFileInputRef.current.click()}
                    disabled={scopeLoading}
                    className="py-2 px-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                  >
                    {scopeLoading ? (
                      <>Loading...</>
                    ) : (
                      <>
                        <FileSpreadsheet size={18} />
                        Upload File
                      </>
                    )}
                  </button>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={currentScopeItem}
                      onChange={(e) => setCurrentScopeItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addScopeItem()}
                      className={`flex-1 max-lg:min-w-0 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300'
                      }`}
                      placeholder="Or type manually"
                    />
                    <button
                      onClick={addScopeItem}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 max-lg:shrink-0 max-md:flex max-md:items-center max-md:justify-center"
                      aria-label="Add scope item"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>

                {scopeError && (
                  <div className={`mb-3 p-2 rounded-md flex items-center gap-2 text-sm ${
                    darkMode ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <AlertCircle size={16} />
                    {scopeError}
                  </div>
                )}
                
                <div className="space-y-2 max-h-64 overflow-y-auto max-md:max-h-none max-md:overflow-visible">
                  <table className="w-full text-sm max-md:block max-md:text-base">
                    <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'} sticky top-0 max-md:hidden`}>
                      <tr>
                        <th className="text-left p-2">Description</th>
                        <th className="text-center p-2 w-20">Qty</th>
                        <th className="text-center p-2 w-24">Unit</th>
                        <th className="text-center p-2 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="max-md:block">
                      {offerData.scopeOfSupply.map((item, index) => (
                        <tr
                          key={index}
                          className={`border-b ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} max-md:block max-md:rounded-lg max-md:border max-md:p-3 max-md:mb-3 ${darkMode ? 'max-md:bg-gray-700/40' : 'max-md:bg-gray-50'}`}
                        >
                          <td className="p-2 max-md:block max-md:p-0 max-md:mb-2">
                            <span className="hidden max-md:block text-xs font-medium mb-1 opacity-70">
                              Item {index + 1}
                            </span>
                            {editingIndex === index ? (
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && saveEditingScopeItem()}
                                className={`w-full px-2 py-1 border rounded ${
                                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'
                                }`}
                                autoFocus
                              />
                            ) : (
                              <span className="break-words">{item.description}</span>
                            )}
                          </td>
                          <td className="p-2 text-center max-md:inline-block max-md:w-[30%] max-md:p-0 max-md:pr-2 max-md:align-top max-md:text-left">
                            <span className="hidden max-md:block text-xs font-medium mb-1 opacity-70">Qty</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={item.quantity}
                              onChange={(e) => updateScopeQuantity(index, e.target.value)}
                              className={`w-full px-1 py-1 border rounded text-center ${
                                darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'
                              }`}
                              aria-label={`Quantity for item ${index + 1}`}
                            />
                          </td>
                          <td className="p-2 text-center max-md:inline-block max-md:w-[34%] max-md:p-0 max-md:pr-2 max-md:align-top max-md:text-left">
                            <span className="hidden max-md:block text-xs font-medium mb-1 opacity-70">Unit</span>
                            <select
                              value={item.unit}
                              onChange={(e) => updateScopeUnit(index, e.target.value)}
                              className={`w-full px-1 py-1 border rounded ${
                                darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'
                              }`}
                              aria-label={`Unit for item ${index + 1}`}
                            >
                              <option value="Set">Set</option>
                              <option value="Unit">Unit</option>
                              <option value="Lot">Lot</option>
                              <option value="Package">Package</option>
                              <option value="Pcs">Pcs</option>
                              <option value="EA">EA</option>
                              <option value="Service">Service</option>
                            </select>
                          </td>
                          <td className="p-2 text-center max-md:block max-md:w-full max-md:p-0 max-md:mt-3 max-md:text-left">
                            <span className="hidden max-md:block text-xs font-medium mb-1 opacity-70">Actions</span>
                            {editingIndex === index ? (
                              <div className="flex justify-center gap-1 max-md:justify-start max-md:gap-4">
                                <button
                                  onClick={saveEditingScopeItem}
                                  className="text-green-600 hover:text-green-800 max-md:inline-flex max-md:items-center max-md:justify-center max-md:w-11 max-md:h-11 max-md:shrink-0"
                                  aria-label="Save item"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  onClick={cancelEditingScopeItem}
                                  className="text-gray-600 hover:text-gray-800 max-md:inline-flex max-md:items-center max-md:justify-center max-md:w-11 max-md:h-11 max-md:shrink-0"
                                  aria-label="Cancel editing"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-center gap-1 max-md:justify-start max-md:gap-4">
                                <button
                                  onClick={() => startEditingScopeItem(index)}
                                  className="text-blue-600 hover:text-blue-800 max-md:inline-flex max-md:items-center max-md:justify-center max-md:w-11 max-md:h-11 max-md:shrink-0"
                                  aria-label={`Edit item ${index + 1}`}
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => removeScopeItem(index)}
                                  className="text-red-600 hover:text-red-800 max-md:inline-flex max-md:items-center max-md:justify-center max-md:w-11 max-md:h-11 max-md:shrink-0"
                                  aria-label={`Remove item ${index + 1}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className="font-semibold mb-3">Images</h3>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current.click()}
                  className={`w-full py-3 border-2 border-dashed rounded-md hover:border-blue-500 flex items-center justify-center gap-2 ${
                    darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'
                  }`}
                >
                  <Upload size={20} />
                  Upload Power Plant Images
                </button>
                
                {offerData.images.length > 0 && (
                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    💡 Drag and drop images to reorder them. Numbers show the sequence for PDF export.
                  </p>
                )}
                
                <div className="grid grid-cols-3 gap-3 mt-3 max-md:grid-cols-2">
                  {offerData.images
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((image, index) => (
                    <div 
                      key={image.id} 
                      className={`relative group cursor-move transition-all duration-200 ${
                        draggedImage === image.id ? 'opacity-50 transform scale-95' : 'opacity-100'
                      }`}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, image.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, image.id)}
                      onDragEnd={handleDragEnd}
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (!el.dataset.retry) {
                            el.dataset.retry = '1';
                            try {
                              const url = new URL(el.src);
                              if (!url.searchParams.has('download')) {
                                url.searchParams.set('download', '1');
                                el.src = url.toString();
                              }
                            } catch (_) {}
                          }
                        }}
                        className="w-full h-24 object-cover rounded shadow-sm border-2 border-transparent hover:border-blue-300 transition-colors max-md:h-32"
                      />
                      
                      {/* Order number badge */}
                      <div className="absolute top-1 left-1 bg-white text-black w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-md border">
                        {image.order || index + 1}
                      </div>
                      
                      {/* Rotation buttons */}
                      <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            rotateImage(image.id, -90);
                          }}
                          className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded shadow-sm border border-gray-300 transition-all hover:scale-105"
                          title="Rotate Left 90°"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M7 11L3 7m0 0l4-4m-4 4h16a4 4 0 014 4v6a4 4 0 01-4 4H8" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            rotateImage(image.id, 90);
                          }}
                          className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded shadow-sm border border-gray-300 transition-all hover:scale-105"
                          title="Rotate Right 90°"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M17 11l4-4m0 0l-4-4m4 4H5a4 4 0 00-4 4v6a4 4 0 004 4h11" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Remove button */}
                      <button
                        onClick={() => removeImage(image.id)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 max-md:opacity-100 max-md:flex max-md:items-center max-md:justify-center"
                        aria-label="Remove image"
                      >
                        <Trash2 size={12} />
                      </button>
                      
                      {/* Drag handle indicator */}
                      <div className="absolute bottom-1 right-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                          <div className="w-1 h-1 bg-current rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className="font-semibold mb-3">Branding</h3>
                
                <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                  <div>
                    <label className={getLabelClasses()}>
                      Logo Image
                    </label>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => logoInputRef.current.click()}
                      className={`w-full py-2 px-3 border rounded-md text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100' : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {offerData.logoImage ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    {offerData.logoImage && (
                      <div className="mt-2">
                        <div className={`p-3 border rounded ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cover Page Preview:</p>
                          <div className="flex justify-center">
                            <img 
                              src={offerData.logoImage} 
                              alt="Logo preview" 
                              style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain' }}
                              className="border border-gray-300"
                            />
                          </div>
                          <p className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Aspect ratio preserved
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className={getLabelClasses()}>
                      Watermark
                    </label>
                    <input
                      ref={watermarkInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleWatermarkUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => watermarkInputRef.current.click()}
                      className={`w-full py-2 px-3 border rounded-md text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100' : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {offerData.watermarkImage ? 'Change Watermark' : 'Upload Watermark'}
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <label className={getLabelClasses()}>
                    Preset Branding (by Company)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => applyBrandingPreset(selectedCompany)}
                      disabled={!selectedCompany || !brandingPresets[selectedCompany]}
                      className={`py-2 px-3 border rounded-md text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100' : 'border-gray-300 hover:bg-gray-50'
                      } ${(!selectedCompany || !brandingPresets[selectedCompany]) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Use {brandingPresets[selectedCompany]?.label || 'Company'} Preset
                    </button>
                    <button
                      type="button"
                      onClick={() => setOfferData(prev => ({ ...prev, logoImage: null, watermarkImage: null }))}
                      className={`py-2 px-3 border rounded-md text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100' : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Clear Branding
                    </button>
                  </div>
                  <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Put preset files in `public/branding/` (see filenames in `brandingPresets`).
                  </p>
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Building size={20} />
                  Company Details
                </h3>
                
                {/* Company Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Quick Load Company:</label>
                  <div className="flex gap-2">
                    <select
                      value={selectedCompany}
                      onChange={(e) => {
                        setSelectedCompany(e.target.value);
                        loadCompanyDetails(e.target.value);
                      }}
                      className={getInputClasses()}
                    >
                      <option value="">Select Company...</option>
                      <option value="fin-marine">Fin Marine & Energy Solutions Ltd.</option>
                      <option value="fin-marine-fzco">Fin Marine FZCO Dubai</option>
                      <option value="atlantis-marine">Atlantis Marine</option>
                      <option value="used-power-plants">Used Power Plants</option>
                    </select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <MobileLabel darkMode={darkMode}>Company Name</MobileLabel>
                    <input
                      type="text"
                      value={offerData.companyDetails.name}
                      onChange={(e) => handleCompanyDetailChange('name', e.target.value)}
                      className={getInputClasses()}
                      placeholder="Company Name"
                    />
                  </div>
                  
                  <div>
                    <MobileLabel darkMode={darkMode}>Address Line 1</MobileLabel>
                    <input
                      type="text"
                      value={offerData.companyDetails.addressLine1}
                      onChange={(e) => handleCompanyDetailChange('addressLine1', e.target.value)}
                      className={getInputClasses()}
                      placeholder="Address Line 1"
                    />
                  </div>
                  
                  <div>
                    <MobileLabel darkMode={darkMode}>Address Line 2</MobileLabel>
                    <input
                      type="text"
                      value={offerData.companyDetails.addressLine2}
                      onChange={(e) => handleCompanyDetailChange('addressLine2', e.target.value)}
                      className={getInputClasses()}
                      placeholder="Address Line 2"
                    />
                  </div>
                  
                  <div>
                    <MobileLabel darkMode={darkMode}>City, Country</MobileLabel>
                    <input
                      type="text"
                      value={offerData.companyDetails.addressLine3}
                      onChange={(e) => handleCompanyDetailChange('addressLine3', e.target.value)}
                      className={getInputClasses()}
                      placeholder="City, Country"
                    />
                  </div>
                  
                  <div>
                    <MobileLabel darkMode={darkMode}>Company Email</MobileLabel>
                    <input
                      type="email"
                      inputMode="email"
                      value={offerData.companyDetails.email}
                      onChange={(e) => handleCompanyDetailChange('email', e.target.value)}
                      className={getInputClasses()}
                      placeholder="Company Email"
                    />
                  </div>
                </div>
              </div>

              <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex justify-between items-center mb-2 max-md:flex-col max-md:items-stretch max-md:gap-2">
                <label className={getLabelClasses()}>
                  Additional Notes
                </label>
                  <button
                    type="button"
                    onClick={() => addBulletPoint('additionalNotes')}
                    className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1 text-sm max-md:justify-center max-md:text-base"
                  >
                    <Plus size={16} />
                    Add Note
                  </button>
                </div>
                {(Array.isArray(offerData.additionalNotes) ? offerData.additionalNotes : ['']).map((note, index) => (
                  <div key={`additional-note-${index}`} className="flex gap-2 mb-2">
                    <div className="flex-1 flex items-center gap-2 max-lg:min-w-0">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>•</span>
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => updateBulletPoint('additionalNotes', index, e.target.value)}
                  className={getInputClasses('max-lg:min-w-0')}
                        placeholder="Enter additional note..."
                />
              </div>
                    {Array.isArray(offerData.additionalNotes) && offerData.additionalNotes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBulletPoint('additionalNotes', index)}
                        className="px-2 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center max-md:justify-center max-md:shrink-0"
                        aria-label="Remove note"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div
            ref={previewPanelRef}
            className={`${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'} rounded-lg shadow-md p-6 overflow-auto max-h-[90vh] max-lg:overflow-visible max-lg:max-h-none max-lg:p-4 ${mobileView === 'preview' ? '' : 'max-lg:hidden'}`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold max-lg:hidden">Preview</h2>
              <div className="flex gap-2 max-lg:grid max-lg:grid-cols-2 max-lg:w-full">
                <button
                  onClick={generatePDF}
                  disabled={pdfGenerating}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 disabled:bg-gray-400 max-lg:justify-center max-md:px-2"
                >
                  <Download size={18} />
                  {pdfGenerating ? 'Generating...' : 'HTML to PDF'}
                </button>
                <button
                  onClick={generateAdvancedPDFDoc}
                  disabled={pdfGenerating}
                  aria-busy={pdfGenerating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:bg-gray-400 max-lg:justify-center max-md:px-2"
                >
                  <FilePlus size={18} />
                  {pdfGenerating ? 'Generating…' : 'Advanced PDF'}
                </button>
              </div>
            </div>
            
            <div id="offer-preview" className="border p-8 bg-white relative text-black max-lg:p-4">
              {/* Watermark for preview - matching PDF 20% opacity */}
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.2] pointer-events-none max-lg:overflow-hidden">
                {offerData.watermarkImage ? (
                  <img src={offerData.watermarkImage} alt="Watermark" className="max-w-lg transform rotate-[-45deg] max-lg:max-w-[75%]" />
                ) : (
                  <div className="transform rotate-[-45deg]">
                    <div className="text-6xl font-bold max-md:text-4xl" style={{color: '#2B3E5C', opacity: '1'}}>ATLANTIS</div>
                  </div>
                )}
              </div>
              
              <div className="relative z-10">
                <div className="header">
                  {offerData.logoImage && (
                    <img src={offerData.logoImage} alt="Company Logo" className="mx-auto mb-6 max-h-32" style={{maxHeight: '150px'}} />
                  )}
                  <h1 className="text-2xl font-bold mb-2">Quotation for:</h1>
                  <h2 className="text-xl font-semibold" style={{color: '#2B3E5C'}}>
                    {offerData.plantTitle || 'Power Plant'}
                  </h2>
                  <h3 className="text-lg text-gray-600">
                    {offerData.plantCapacity || 'Capacity'} Complete {offerData.fuelType || 'Gas'} Power Plant
                  </h3>
                </div>

                <div className="company-info max-lg:break-words">
                  <p><strong>Quotation No:</strong> {offerData.quotationNo || 'XXXXXXXX'}</p>
                  <p><strong>Date:</strong> {offerData.date}</p>
                  <div className="mt-3">
                    <p><strong>Attn of:</strong></p>
                    <p className="font-semibold">{offerData.clientName || 'Client Name'}</p>
                    <p className="whitespace-pre-line">{offerData.clientAddress || 'Client Address'}</p>
                    <p>{offerData.clientEmail || 'client@email.com'}</p>
                  </div>
                </div>

                <div className="mt-6">
                  <p><strong>Subject:</strong></p>
                  <p className="text-lg">
                    Quotation of {offerData.plantCapacity || 'XX MW'} - {offerData.plantTitle || 'Power Plant'}
                  </p>
                </div>

                <div className="mt-6">
                  <p>Dear Sir,</p>
                  <p className="mt-2">
                    Thank you for your enquiry of this {offerData.plantCapacity || 'XX MW'} {(offerData.fuelType || 'gas').toLowerCase()} power plant 
                    for your project. We are glad to propose the below for your consideration.
                  </p>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3">
                    1) To Supply of {offerData.plantCapacity || 'XX MW'} - {offerData.plantTitle || 'Power Plant'} 
                    and Related Auxiliary Equipment:
                  </h3>
                  
                  {/* Display equipment details */}
                  {(offerData.equipment && offerData.equipment.length > 0) ? (
                    offerData.equipment.map((eq, eqIndex) => (
                      <div key={eq.id} className="mb-6">
                        {offerData.equipment.length > 1 && (
                          <h4 className="text-md font-semibold mb-2" style={{color: '#2B3E5C'}}>
                            Equipment #{eqIndex + 1}: {eq.quantity && eq.quantity !== '1' ? `${eq.quantity} x ` : ''}{eq.equipmentType || 'Equipment'}
                            {eq.engineManufacturer && eq.engineType ? ` - ${eq.engineManufacturer} ${eq.engineType}` : ''}
                          </h4>
                        )}
                        <table className="specs-table">
                          <tbody>
                            {eq.equipmentType && (
                              <tr>
                                <td>Equipment Type:</td>
                                <td>{eq.equipmentType}</td>
                              </tr>
                            )}
                            {eq.quantity && (
                              <tr>
                                <td>Quantity:</td>
                                <td>{eq.quantity} {eq.quantity === '1' ? 'Unit' : 'Units'}</td>
                              </tr>
                            )}
                            {eq.engineManufacturer && (
                              <tr>
                                <td>Manufacturer:</td>
                                <td>{eq.engineManufacturer}</td>
                              </tr>
                            )}
                            {eq.engineType && (
                              <tr>
                                <td>Model/Type:</td>
                                <td>{eq.engineType}</td>
                              </tr>
                            )}
                            {eq.engineOutput && (
                              <tr>
                                <td>Output:</td>
                                <td>{eq.engineOutput} KW</td>
                              </tr>
                            )}
                            {eq.yearBuilt && (
                              <tr>
                                <td>Year Built:</td>
                                <td>{eq.yearBuilt}</td>
                              </tr>
                            )}
                            {eq.workingHours && (
                              <tr>
                                <td>Working Hours:</td>
                                <td>{eq.workingHours}</td>
                              </tr>
                            )}
                            {eq.alternatorType && (
                              <tr>
                                <td>Alternator/Generator:</td>
                                <td>{eq.alternatorType}</td>
                              </tr>
                            )}
                            {(eq.voltage || eq.frequency) && (
                              <tr>
                                <td>Voltage / Frequency:</td>
                                <td>
                                  {eq.voltage ? `${eq.voltage} KV` : ''} 
                                  {eq.voltage && eq.frequency ? ' / ' : ''} 
                                  {eq.frequency || ''}
                                </td>
                              </tr>
                            )}
                            {eq.powerRating && (
                              <tr>
                                <td>Power Rating:</td>
                                <td>{eq.powerRating}</td>
                              </tr>
                            )}
                            {eq.speed && (
                              <tr>
                                <td>Speed:</td>
                                <td>{eq.speed} rpm</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ))
                  ) : (
                    // Fallback to old single equipment format for backward compatibility
                    <table className="specs-table">
                      <tbody>
                        {offerData.engineManufacturer && (
                          <tr>
                            <td>Engine manufacturer:</td>
                            <td>{offerData.engineManufacturer}</td>
                          </tr>
                        )}
                        {offerData.engineType && (
                          <tr>
                            <td>Engine Type:</td>
                            <td>{offerData.engineType}</td>
                          </tr>
                        )}
                        {offerData.engineOutput && (
                          <tr>
                            <td>Engine output:</td>
                            <td>{offerData.engineOutput} KW</td>
                          </tr>
                        )}
                        {offerData.yearBuilt && (
                          <tr>
                            <td>Year of built:</td>
                            <td>{offerData.yearBuilt}</td>
                          </tr>
                        )}
                        {offerData.workingHours && (
                          <tr>
                            <td>Working Hours:</td>
                            <td>{offerData.workingHours}</td>
                          </tr>
                        )}
                        {offerData.alternatorType && (
                          <tr>
                            <td>Alternator Type:</td>
                            <td>{offerData.alternatorType}</td>
                          </tr>
                        )}
                        {(offerData.voltage || offerData.frequency) && (
                          <tr>
                            <td>Volt. / Hz / Phase:</td>
                            <td>{offerData.voltage} KV / {offerData.frequency}</td>
                          </tr>
                        )}
                        {offerData.powerRating && (
                          <tr>
                            <td>Power Rating:</td>
                            <td>{offerData.powerRating}</td>
                          </tr>
                        )}
                        {offerData.speed && (
                          <tr>
                            <td>Speed:</td>
                            <td>{offerData.speed}rpm</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                  
                  {/* Fuel Type - show at plant level */}
                  {offerData.fuelType && (
                    <div className="mt-4">
                      <p><strong>Fuel Type:</strong> {offerData.fuelType}</p>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold">2) Price</h3>
                  <p className="mt-2">Total price {offerData.price || 'EUR XXXXX'} in net, Ex works.</p>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold">3) Payment</h3>
                  {Array.isArray(offerData.paymentTerms) && offerData.paymentTerms.filter(term => term.trim()).length > 0 ? (
                    <ul className="mt-2 list-none">
                      {offerData.paymentTerms.filter(term => term.trim()).map((term, index) => (
                        <li key={index} className="ml-4 max-lg:ml-0"><span className="max-lg:hidden">• </span>{term}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2">Payment terms to be discussed.</p>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold">4) Delivery time</h3>
                  <p className="mt-2">{offerData.deliveryTime || 'Ex-stock installed in Power Plant'}</p>
                </div>

                {offerData.scopeOfSupply.length > 0 && (
                  <div className="scope-section">
                    <h3 className="text-lg font-semibold mb-3">5) Scope of supply</h3>
                    <table className="scope-table">
                      <thead>
                        <tr>
                          <th className="w-[60px] max-md:w-9">No</th>
                          <th>Description</th>
                          <th className="w-[80px] max-md:w-12">Qty</th>
                          <th className="w-[100px] max-md:w-14">Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offerData.scopeOfSupply.map((item, index) => (
                          <tr key={index}>
                            <td style={{textAlign: 'center'}}>{index + 1}</td>
                            <td>{item.description}</td>
                            <td style={{textAlign: 'center'}}>{item.quantity}</td>
                            <td style={{textAlign: 'center'}}>{item.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {Array.isArray(offerData.additionalNotes) && offerData.additionalNotes.filter(note => note.trim()).length > 0 && (
                  <div className="mt-6">
                    <ul className="list-none">
                      {offerData.additionalNotes.filter(note => note.trim()).map((note, index) => (
                        <li key={index} className="ml-4">• {note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {offerData.images.length > 0 && (
                  <div className="images-section page-break">
                    <h3 className="text-lg font-semibold mb-4">
                      {offerData.plantCapacity || 'XX MW'} {offerData.engineType || 'Power Plant'} complete power plant
                    </h3>
                    <div>
                      {offerData.images
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((image, index) => (
                        <div key={image.id} className="image-container">
                          <img 
                            src={image.url} 
                            alt={`Plant image ${image.order || index + 1}`}
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              // Try adding download=1 on error for some signed URL viewers
                              const el = e.currentTarget;
                              if (!el.dataset.retry) {
                                el.dataset.retry = '1';
                                try {
                                  const url = new URL(el.src);
                                  if (!url.searchParams.has('download')) {
                                    url.searchParams.set('download', '1');
                                    el.src = url.toString();
                                  }
                                } catch (_) {}
                              }
                            }}
                            className="mx-auto max-w-full h-auto rounded shadow-lg"
                            style={{maxHeight: '500px'}}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer for preview */}
              <div className="mt-16 pt-4 border-t text-center text-sm text-gray-600 max-lg:break-words">
                <strong>{offerData.companyDetails.name}</strong><br/>
                {offerData.companyDetails.addressLine1} | {offerData.companyDetails.addressLine2}<br/>
                {offerData.companyDetails.addressLine3}
                {offerData.companyDetails.email && (
                  <div className="mt-1">{offerData.companyDetails.email}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phone/tablet only: always-reachable switch between the form and the
          preview. Presentational only - it just picks which panel is shown. */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}
        style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.08)' }}
      >
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => showMobilePanel('form')}
            aria-pressed={mobileView === 'form'}
            className={mobileTabClasses('form')}
          >
            <Edit2 size={18} />
            Form
          </button>
          <button
            type="button"
            onClick={() => showMobilePanel('preview')}
            aria-pressed={mobileView === 'preview'}
            className={mobileTabClasses('preview')}
          >
            <FileText size={18} />
            Preview
          </button>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0 })}
            aria-label="Back to top"
            title="Back to top"
            className={`flex items-center justify-center rounded-md px-3 border ${
              darkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <ArrowUp size={20} />
          </button>
        </div>
      </div>

      <style jsx>{`
        .header { 
          text-align: center; 
          margin-bottom: 30px;
          border-bottom: 3px solid #2B3E5C;
          padding-bottom: 20px;
        }
        
        .company-info {
          background: #f0f0f0;
          padding: 15px;
          margin: 20px 0;
          border-radius: 5px;
        }
        
        .specs-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        
        .specs-table td {
          padding: 8px;
          border-bottom: 1px solid #ddd;
        }
        
        .specs-table td:first-child {
          font-weight: bold;
          width: 40%;
        }
        
        .scope-section {
          margin: 30px 0;
        }
        
        .scope-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .scope-table th {
          background: #2B3E5C;
          color: white;
          padding: 10px;
          text-align: left;
          border: 1px solid #2B3E5C;
        }
        
        .scope-table td {
          padding: 8px;
          border: 1px solid #ddd;
        }
        
        .scope-table th:nth-child(3),
        .scope-table th:nth-child(4) {
          text-align: center;
        }
        
        .images-section {
          margin-top: 40px;
        }
        
        .image-container {
          text-align: center;
          margin-bottom: 40px;
          break-inside: avoid;
        }
        
        .image-container img {
          max-width: 80%;
          height: auto;
          border: 1px solid #ddd;
          padding: 5px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        /* ---- Phone / small-tablet only (desktop >= 1024px is untouched) ---- */
        @media (max-width: 1023px) {
          /* 16px minimum stops iOS Safari from zooming in on focus */
          .pp-builder input:not([type="file"]),
          .pp-builder select {
            font-size: 16px;
            min-height: 44px;
          }
          .pp-builder textarea {
            font-size: 16px;
            min-height: 7rem;
          }
          .pp-builder button {
            min-height: 44px;
            min-width: 44px;
          }
          .pp-builder ::placeholder {
            color: #4b5563;
            opacity: 1;
          }
          .pp-builder.dark ::placeholder,
          .dark .pp-builder ::placeholder {
            color: #cbd5e1;
            opacity: 1;
          }
          /* Keep the generated offer inside the screen */
          .pp-builder #offer-preview,
          .pp-builder #offer-preview p,
          .pp-builder #offer-preview li,
          .pp-builder #offer-preview td,
          .pp-builder #offer-preview th {
            overflow-wrap: anywhere;
          }
          .pp-builder .company-info {
            padding: 12px;
          }
          .pp-builder .scope-table,
          .pp-builder .specs-table {
            font-size: 13px;
            table-layout: fixed;
          }
          .pp-builder .scope-table th,
          .pp-builder .scope-table td {
            padding: 6px 4px;
          }
          .pp-builder .specs-table td {
            padding: 6px 4px;
          }
          .pp-builder .image-container img {
            max-width: 100%;
          }
          /* Clear the fixed Form / Preview bar, including the home-indicator inset. */
          .pp-builder {
            padding-bottom: calc(1px + 0.5rem + 44px + max(0.5rem, env(safe-area-inset-bottom, 0px))) !important;
          }
          .pp-builder label,
          .pp-builder .pp-mobile-label {
            text-align: left;
          }
          .pp-builder #offer-preview ul {
            list-style: none;
            padding-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PowerPlantOfferMaker; 